import * as PIXI from 'pixi.js';
import type { WordElementMetrics } from './WordElementMetrics';
import type { WordElementTexture } from './WordElementTexture';

/**
 * This class is a Sprite built from a WordElementTexture. It is inherently lighter than using a texture and copies
 * are cheap.
 * The added value (relative to PIXI.Sprite) is that we store our WordElementMetrics, just like the WordElementTexture.
 * This makes it easier to use both interchangeably. A major difference between WordElementSprite and
 * WordElementTexture is that the Sprite cannot be modified by setting the text.
 */
export class WordElementSprite extends PIXI.Sprite {
    #metrics: WordElementMetrics = null;

    /**
     * Initialize a Sprite from a WordElementTexture.
     * @param {WordElementTexture} wordElementTexture - texture to build the sprite with
     *
     */
    constructor(wordElementTexture: WordElementTexture) {
        super(wordElementTexture._texture);
        this._texture = wordElementTexture._texture;
        this.#metrics = wordElementTexture.metrics;
    }

    set metrics(value: WordElementMetrics) {
        throw new Error('There was an attempt to modify the metrics of a WordElementSprite (which cannot be changed).');
    }

    /**
     * Get the metrics of the WordElementTexture which was used to generate this sprite.
     */
    get metrics(): WordElementMetrics {
        return this.#metrics;
    }

    set text(value: string) {
        throw new Error('There was an attempt to modify the text of a WordElementSprite (which cannot be changed)');
    }

    /**
     * Get the text of the WordElementTexture which was used to generate this sprite.
     */
    get text(): string {
        return this.#metrics.text;
    }

    override get destroyed(): boolean {
        return this._destroyed;
    }
}
